/** Asking a supplier for a figure - **ADR-0151**, on ADR-0040's exchange.
 *
 * `POST /supplier-exchange/requests` existed from the start and no screen called it, so a buyer could
 * receive a supplier's answer and could not ask for one (journey walk, 2026-10-31). This is that
 * control, in the engine's own vocabulary, with the same refusals the route makes.
 *
 * ## Who can be asked, and why the list is short
 *
 * **Only a supplier this workspace already has an exchange with.** The supplier is a different
 * tenant, and this workspace may not read its `organization` row (ADR-0040): there is no supplier
 * directory to search, no name to show and no invitation to send. A workspace id is therefore only
 * known from an earlier request, and that is the list this offers. A workspace that has never dealt
 * with a supplier is told so, and told how the first request is made (through the API, with the
 * workspace id the supplier gives) rather than shown a control that cannot work (design rule 6).
 * The account page passes the one supplier it is about, so there is no choice to make there.
 *
 * ## What is asked, and what is never defaulted
 *
 * The site, the kind of activity and the fuel or carrier are read from the factor library for that
 * site's jurisdiction (`manualCarriers`, F06's rule: the set is the authority on what is
 * supported), and none has a default - a diesel request filed because nobody changed a dropdown is
 * the defect that rule exists for. The site is **required here** though the route makes it optional:
 * it is what says which factor set can price the answer, so the form can offer only what the
 * product could turn into a figure, and asking a supplier for something it could not price spends
 * the one favour a buyer gets to ask.
 *
 * ## The note is the buyer's words, and goes to another company
 *
 * It is shown to the supplier, never ledgered (rule 4), and the form says both.
 *
 * ## Not retried
 *
 * A request that did not complete may exist, and a second would be two asks of the same supplier.
 * The failure sentence tells the reader to look at the list.
 */

import { useState } from "react";

import { useMe } from "../api/auth";
import { useFacilities } from "../api/evidence";
import { useFactorLibrary } from "../api/factors";
import type { Exchange } from "../api/suppliers";
import { useRaiseRequest } from "../api/suppliers";
import { Loaded, WriteFailed } from "../components/Loaded";
import { carriersIn, categoriesIn, setFor } from "../lib/manualCarriers";
import { knownSuppliers, supplierLabels, supplierName, supplierNames } from "./supplierLabels";
import styles from "./Screen.module.css";
import own from "./Suppliers.module.css";

/** Mirrored from `api/suppliers.py`'s `MAY_COLLECT`: asking is collection work. */
const MAY_COLLECT: readonly string[] = ["admin", "approver", "contributor"];

function today(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

export function RaiseRequest({
  rows,
  supplierId,
}: {
  /** Every exchange row this workspace can read: the source of who is known. */
  rows: readonly Exchange[];
  /** The one supplier, when the caller is that supplier's own page. */
  supplierId?: string;
}) {
  const { data: me } = useMe();
  const mayCollect = me ? MAY_COLLECT.includes(me.role) : undefined;
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);

  // While `/auth/me` is in flight nothing is said: a sentence about who can ask, shown to a reader
  // who can, would be a flash of the wrong thing.
  if (mayCollect === undefined) return null;
  if (!mayCollect) {
    return (
      <p className={styles.note} data-testid="raise-who-can">
        An Admin, Approver or Contributor asks a supplier for a figure.
      </p>
    );
  }

  const numbers = supplierNames(rows);
  const labelled = supplierLabels(rows);
  const known = knownSuppliers(rows).map((entry) => ({
    ...entry,
    name: supplierName(entry.id, numbers),
    label: labelled.get(entry.id),
  }));
  if (supplierId === undefined && known.length === 0) {
    return (
      <p className={styles.note} data-testid="raise-no-supplier">
        <strong>No supplier to ask yet.</strong> A supplier is a workspace of its own, and EmitIQ has no
        supplier directory and no invitation to send, so this workspace can only ask a supplier it has
        already dealt with. The first request to a new supplier is raised through the API with the id
        of the supplier&rsquo;s workspace; after that the supplier is listed here.
      </p>
    );
  }

  return (
    <div className={own.form} data-testid="raise-request-area">
      {open ? (
        <RaiseForm
          known={known}
          fixed={supplierId}
          onDone={() => {
            setOpen(false);
            setDone(true);
          }}
          onCancel={() => setOpen(false)}
        />
      ) : (
        <>
          <button
            type="button"
            className={styles.secondary}
            data-testid="raise-open"
            onClick={() => {
              setDone(false);
              setOpen(true);
            }}
          >
            Ask a supplier for a figure
          </button>
          {done ? (
            <p className={styles.note} role="status" data-testid="raise-done">
              <strong>Asked.</strong> The request is in the list as <em>Waiting for an answer</em>, and the
              supplier sees it in its own workspace. Nothing is sent by email.
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}

function RaiseForm({
  known,
  fixed,
  onDone,
  onCancel,
}: {
  known: ReadonlyArray<{ id: string; asked: number; name: string; label: string | undefined }>;
  fixed: string | undefined;
  onDone: () => void;
  onCancel: () => void;
}) {
  const raise = useRaiseRequest();
  const facilitiesRead = useFacilities();
  const library = useFactorLibrary();

  const [chosen, setChosen] = useState("");
  const [site, setSite] = useState("");
  const [category, setCategory] = useState("");
  const [carrier, setCarrier] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [due, setDue] = useState("");
  const [note, setNote] = useState("");
  const [typedLabel, setTypedLabel] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<unknown>(null);
  const { data: me } = useMe();

  const supplier = fixed ?? chosen;
  // What this workspace already calls the supplier. A Contributor may name a supplier that has no
  // label and may not change one (the server refuses it), so the field is offered only then.
  const currentLabel = known.find((entry) => entry.id === supplier)?.label;
  const mayRename = me?.role === "admin" || me?.role === "approver";
  const offerLabel = supplier !== "" && (mayRename || currentLabel === undefined);
  const label = typedLabel ?? currentLabel ?? "";
  const facility = facilitiesRead.data?.find((candidate) => candidate.id === site);
  const factorSet = facility && library.data ? setFor(library.data, facility.jurisdiction) : undefined;
  const categoryOptions = factorSet ? categoriesIn(factorSet) : [];
  const carrierOptions = factorSet && category ? carriersIn(factorSet, category) : [];
  const ready =
    supplier !== "" &&
    site !== "" &&
    category !== "" &&
    carrierOptions.includes(carrier) &&
    from !== "" &&
    to !== "" &&
    to >= from;

  function submit() {
    setRefusal(null);
    raise
      .mutateAsync({
        supplier_org_id: supplier,
        facility_id: site,
        category,
        fuel_or_carrier: carrier,
        period_start: from,
        period_end: to,
        ...(due ? { due_on: due } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(offerLabel && label.trim() && label.trim() !== (currentLabel ?? "")
          ? { supplier_label: label.trim() }
          : {}),
      })
      .then(onDone)
      .catch((failure: unknown) => setRefusal(failure));
  }

  return (
    <form
      className={own.form}
      data-testid="raise-request"
      onSubmit={(event) => {
        event.preventDefault();
        if (ready && !raise.isPending) submit();
      }}
    >
      {fixed === undefined ? (
        <label className={styles.field}>
          <span>Which supplier?</span>
          <select
            value={chosen}
            onChange={(event) => {
              setChosen(event.target.value);
              setTypedLabel(null);
            }}
            data-testid="raise-supplier"
          >
            <option value="">Choose a supplier…</option>
            {known.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.name} · asked {entry.asked} time{entry.asked === 1 ? "" : "s"}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      <Loaded
        query={facilitiesRead}
        of="The list of your sites"
        empty="No sites are set up yet. Add one on Organisation before asking for a figure."
      >
        {(sites) => (
          <label className={styles.field}>
            <span>Which of your sites is it for?</span>
            <select
              value={site}
              onChange={(event) => {
                // A different site may be priced by a different set, so what was chosen under the
                // last one no longer means what it did.
                setSite(event.target.value);
                setCategory("");
                setCarrier("");
              }}
              data-testid="raise-site"
            >
              <option value="">Choose a site…</option>
              {sites.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </Loaded>

      <Loaded query={library} of="The list of fuels and carriers">
        {() => (
          <>
            <label className={styles.field}>
              <span>What kind of activity?</span>
              <select
                value={category}
                disabled={categoryOptions.length === 0}
                onChange={(event) => {
                  setCategory(event.target.value);
                  setCarrier("");
                }}
                data-testid="raise-category"
              >
                <option value="">{site ? "Choose a kind of activity…" : "Choose a site first"}</option>
                {categoryOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className={styles.field}>
              <span>Which fuel or carrier?</span>
              <select
                value={carrier}
                disabled={carrierOptions.length === 0}
                onChange={(event) => setCarrier(event.target.value)}
                data-testid="raise-carrier"
              >
                <option value="">
                  {category ? "Choose a fuel or carrier…" : "Choose a kind of activity first"}
                </option>
                {carrierOptions.map((option) => (
                  <option key={option} value={option}>
                    {option.replaceAll("_", " ")}
                  </option>
                ))}
              </select>
            </label>
          </>
        )}
      </Loaded>
      {facility && !factorSet ? (
        <p className={styles.note} data-testid="raise-no-factors">
          EmitIQ has no emission factors loaded for this site&rsquo;s jurisdiction, so an answer about it
          could not be turned into a figure and a supplier should not be asked.
        </p>
      ) : null}

      <label className={styles.field}>
        <span>The period starts</span>
        <input type="date" value={from} onChange={(event) => setFrom(event.target.value)} data-testid="raise-from" />
      </label>
      <label className={styles.field}>
        <span>The period ends</span>
        <input
          type="date"
          value={to}
          min={from || undefined}
          onChange={(event) => setTo(event.target.value)}
          data-testid="raise-to"
        />
      </label>
      <label className={styles.field}>
        <span>Wanted by (optional)</span>
        <input
          type="date"
          value={due}
          min={today()}
          onChange={(event) => setDue(event.target.value)}
          data-testid="raise-due"
        />
      </label>
      {offerLabel ? (
        <label className={styles.field}>
          <span>What you call this supplier (optional)</span>
          <input
            type="text"
            value={label}
            maxLength={120}
            onChange={(event) => setTypedLabel(event.target.value)}
            data-testid="raise-label"
          />
        </label>
      ) : null}
      <label className={styles.field}>
        <span>A note for the supplier (optional)</span>
        <textarea
          value={note}
          rows={3}
          maxLength={500}
          onChange={(event) => setNote(event.target.value)}
          data-testid="raise-note"
        />
      </label>
      <p className={styles.note}>
        The supplier reads your note in its own workspace. It is not put in the ledger; do not put a
        person&rsquo;s details in it. The name you give the supplier is yours: it is not shown to the
        supplier.
      </p>
      {from !== "" && to !== "" && to < from ? (
        <p className={styles.note} role="alert" data-testid="raise-period-order">
          That period ends before it starts.
        </p>
      ) : null}

      <button
        type="submit"
        className={styles.primary}
        data-testid="raise-send"
        disabled={!ready || raise.isPending}
      >
        {raise.isPending ? "Asking…" : "Ask the supplier"}
      </button>
      <button type="button" className={styles.fixLink} onClick={onCancel}>
        Cancel
      </button>
      {/* **No retry.** A request that did not complete may have been created; a second would be two
          asks of one supplier for one figure. The sentence sends the reader to look at the list. */}
      <WriteFailed error={refusal} of="this request" />
    </form>
  );
}
